/**
 * Three-dimensional vectors.
 *
 * Deliberately separate from `Vec2` rather than a generalisation of it. The 2D type is
 * used on every pointer move and every painted vertex; giving it a third component it
 * never needs would cost allocation and clarity everywhere for the benefit of one
 * subsystem.
 *
 * ## Handedness
 *
 * Right-handed, with **y up** and **z toward the viewer**. The y-up choice matches the
 * 2D world — the plane a solid is drawn onto — so a solid's height runs the same way as a
 * point's y coordinate and no axis flips between the two systems.
 */

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export function vec3(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}

export const ZERO: Vec3 = Object.freeze({ x: 0, y: 0, z: 0 });

export function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

export function subtract(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

export function scale(v: Vec3, k: number): Vec3 {
  return { x: v.x * k, y: v.y * k, z: v.z * k };
}

export function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

export function length(v: Vec3): number {
  return Math.hypot(v.x, v.y, v.z);
}

export function normalize(v: Vec3): Vec3 {
  const len = length(v);
  return len === 0 ? ZERO : scale(v, 1 / len);
}

/** Centroid of a set of points. Returns the origin for an empty set. */
export function centroid(points: readonly Vec3[]): Vec3 {
  if (points.length === 0) return ZERO;
  let sum = ZERO;
  for (const point of points) sum = add(sum, point);
  return scale(sum, 1 / points.length);
}

/**
 * The Newell normal of a polygon.
 *
 * Newell's method rather than the cross product of two edges: a tessellated face is not
 * exactly planar once floating point is involved, and picking two edges makes the answer
 * depend on *which* two. Newell averages over the whole ring, so a nearly-planar face gets
 * the normal a human would call correct instead of one that flips when a vertex drifts by
 * 1e-15 — and a flipped normal is a visibly wrong hidden line.
 */
export function newellNormal(points: readonly Vec3[]): Vec3 {
  let x = 0;
  let y = 0;
  let z = 0;

  for (let i = 0; i < points.length; i++) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    if (current === undefined || next === undefined) continue;

    x += (current.y - next.y) * (current.z + next.z);
    y += (current.z - next.z) * (current.x + next.x);
    z += (current.x - next.x) * (current.y + next.y);
  }

  return normalize({ x, y, z });
}

/* --------------------------------------------------------------------- rotation */

export interface Euler {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export const NO_ROTATION: Euler = Object.freeze({ x: 0, y: 0, z: 0 });

/**
 * Applies an X-then-Y-then-Z rotation.
 *
 * Order is fixed and documented because Euler angles do not commute: the same three
 * numbers describe different orientations under a different order, and a saved file that
 * did not pin the order would reopen showing a different solid.
 */
export function rotate(v: Vec3, euler: Euler): Vec3 {
  const cx = Math.cos(euler.x);
  const sx = Math.sin(euler.x);
  const cy = Math.cos(euler.y);
  const sy = Math.sin(euler.y);
  const cz = Math.cos(euler.z);
  const sz = Math.sin(euler.z);

  // X
  const y1 = v.y * cx - v.z * sx;
  const z1 = v.y * sx + v.z * cx;

  // Y
  const x2 = v.x * cy + z1 * sy;
  const z2 = -v.x * sy + z1 * cy;

  // Z
  const x3 = x2 * cz - y1 * sz;
  const y3 = x2 * sz + y1 * cz;

  return { x: x3, y: y3, z: z2 };
}

/**
 * Turns a vector about an arbitrary axis through the origin — Rodrigues' rotation.
 *
 * ## Why this and not an Euler triple
 *
 * Because the axis a figure turns about is one it *names*: the edge `AB`, the giao tuyến of two
 * planes. Expressing "sixty degrees about AB" as three world-axis rotations means solving for
 * them, and the three numbers that come out say nothing a reader could check against the figure.
 * The axis-and-angle form is the sentence the problem is written in.
 *
 * `rotate` above stays what it is: a solid's own orientation really is three dials a user turns,
 * and the file has stored them that way since before this existed.
 *
 * ## The sign
 *
 * Right-handed about `axis`: with the axis pointing at the viewer, a positive angle turns
 * anticlockwise. So reversing the axis reverses the turn, which is what lets a caller offer
 * "the other way" by naming the same two points in the other order — no separate flag, and no
 * question about which of two answers a given number means.
 *
 * `axis` is normalised here rather than demanded of the caller. A caller holding `B − A` should
 * not have to remember; the one that already has a unit vector pays a square root it did not
 * need, which is not a cost worth an easily-forgotten precondition.
 */
export function rotateAbout(v: Vec3, axis: Vec3, radians: number): Vec3 {
  // A zero axis names no rotation. Returning the vector untouched is the only honest answer:
  // `normalize` gives back the origin there, and the formula below would then have scaled `v`
  // by `cos` alone and shrunk the figure for an axis that said nothing.
  if (length(axis) === 0) return v;

  const unit = normalize(axis);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  // v·cos + (k × v)·sin + k(k·v)(1 − cos)
  return add(add(scale(v, cos), scale(cross(unit, v), sin)), scale(unit, dot(unit, v) * (1 - cos)));
}
